import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Seq } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { AppState } from 'src/store/appstate';
import {
  RegionProps,
  selectRegionEntitiesByCompanyId,
} from 'src/store/generated/region-record.generated';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { CommCenterAuthorizationService } from './comm-center-authorization.service';
import { OpsReaderAuthorizationService } from './ops-reader-authorization.service';

@Injectable({ providedIn: 'root' })
export class RegionAuthorizationService {
  constructor(
    private _user: UserService,
    private _store: Store<AppState>,
    private _commCenterAuthorization: CommCenterAuthorizationService,
    private _opsReaderAuthorization: OpsReaderAuthorizationService,
    private _permissions: PermissionsService,
  ) {}

  public isAuthorizedForCompanyCode(regionId: string, companyCode: string) {
    return this.getAuthorizedRegionsByCompanyCode(companyCode).pipe(
      map((regions) => regions.find((r) => r.id === regionId) != null),
    );
  }

  public getAuthorizedRegionsByCompanyCode(companyCode: string) {
    return this._store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(
        switchMap((c) =>
          c != null ? this.getAuthorizedRegions(c.id) : of(Seq.Indexed<RegionProps>()),
        ),
      );
  }

  public getAuthorizedRegions(companyId: string): Observable<Seq.Indexed<RegionProps>> {
    const authorizedCommCenters$ =
      this._commCenterAuthorization.getAuthorizedCommCenters(companyId);
    const regions$ = this._store.select(selectRegionEntitiesByCompanyId(companyId));

    return combineLatest([this._user.user$, authorizedCommCenters$, regions$]).pipe(
      map(([user, authorizedCommCenters, regions]) => {
        if (user == null) {
          return Seq.Indexed<RegionProps>();
        }
        const authorizedCommCenterIds = authorizedCommCenters.map((cc) => cc.id);
        return regions
          .sortBy((_) => _.name)
          .filter((r) => {
            const withCommCenter =
              r.communicationCenterId != null &&
              authorizedCommCenterIds.includes(r.communicationCenterId);
            const withoutCommCenter =
              r.communicationCenterId == null &&
              user.hasPermissionAnyScope(
                this._permissions,
                TcsCustomPermission.AccessRegionsWithoutCommCenterAssignment,
              );
            const isAuthorized = withCommCenter || withoutCommCenter;
            return isAuthorized;
          })
          .valueSeq();
      }),
    );
  }
}
